import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Share2, FileText, Download, Globe, Presentation, ArrowLeft } from "lucide-react";
import { api, API_BASE, resolveFileUrl } from "../lib/api";
import AudioPlayer from "../components/AudioPlayer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

const LANGS = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "pt", label: "Português" },
  { code: "gn", label: "Guaraní" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "ko", label: "한국어" },
];

function fmtTs(t) {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function SermonDetail() {
  const { id } = useParams();
  const [sermon, setSermon] = useState(null);
  const [lang, setLang] = useState("es");
  const [notes, setNotes] = useState("");
  const audioApi = useRef({});

  useEffect(() => {
    api.get(`/sermons/${id}`).then((r) => setSermon(r.data)).catch(() => toast.error("Prédica no encontrada"));
    setNotes(localStorage.getItem(`notes:${id}`) || "");
  }, [id]);

  useEffect(() => { localStorage.setItem(`notes:${id}`, notes); }, [notes, id]);

  const view = useMemo(() => {
    if (!sermon) return null;
    if (lang === "es") return sermon;
    const t = (sermon.translations || {})[lang];
    if (!t) return sermon;
    return { ...sermon, ...t };
  }, [sermon, lang]);

  if (!sermon) {
    return <div className="max-w-5xl mx-auto px-4 py-20 text-center text-[#55594F] text-base">Cargando…</div>;
  }

  const shareWhatsApp = () => {
    const url = window.location.href;
    const text = `${sermon.title} — Pastor Osvaldo Paiva\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-12" data-testid="sermon-detail">
      <Link to="/predicas" className="pill pill-sm pill-outline mb-8" data-testid="btn-back"><ArrowLeft size={16} /> Volver a prédicas</Link>

      <div className="flex flex-wrap gap-2 items-center mb-4 text-base">
        {view.series && <span className="badge-soft">{view.series}</span>}
        {sermon.published_at && (
          <span className="text-[#55594F]">· {new Date(sermon.published_at).toLocaleDateString("es-PY", { day: "2-digit", month: "long", year: "numeric" })}</span>
        )}
      </div>
      <h1 className="hero-headline text-[40px] sm:text-6xl lg:text-[72px] mb-4" data-testid="detail-title">{view.title}</h1>
      {sermon.bible_reference && (
        <div className="font-mono text-lg md:text-xl text-[#A36A00] font-semibold mb-8" data-testid="detail-reference">
          {sermon.bible_reference} <span className="text-[#55594F] font-normal">({sermon.bible_version})</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-8">
        <button onClick={shareWhatsApp} className="pill pill-sm pill-gold" data-testid="btn-share-whatsapp"><Share2 size={16} /> Compartir</button>
        <a href={`${API_BASE}/sermons/${sermon.id}/pdf?lang=${lang}`} target="_blank" rel="noreferrer" className="pill pill-sm pill-outline" data-testid="btn-pdf"><FileText size={16} /> PDF</a>
        <a href={`${API_BASE}/sermons/${sermon.id}/docx?lang=${lang}`} target="_blank" rel="noreferrer" className="pill pill-sm pill-outline" data-testid="btn-docx"><Download size={16} /> Word</a>
        <Link to={`/pulpito/${sermon.id}`} className="pill pill-sm pill-outline" data-testid="btn-pulpit"><Presentation size={16} /> Modo púlpito</Link>
        <div className="flex items-center gap-2 ml-auto">
          <Globe size={18} className="text-[#55594F]" />
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="input-dark !min-h-[48px] !py-0 !px-4 w-auto text-base"
            data-testid="language-selector"
          >
            {LANGS.map((l) => (
              <option key={l.code} value={l.code} disabled={l.code !== "es" && !(sermon.translations || {})[l.code]}>
                {l.label}{l.code !== "es" && !(sermon.translations || {})[l.code] ? " (no disponible)" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-8">
        <AudioPlayer src={sermon.audio_url} apiRef={audioApi} />
      </div>

      {sermon.video_url && (
        <div className="mb-10 rounded-[28px] overflow-hidden border border-[#ECE6D8] bg-black" data-testid="video-player">
          <video src={resolveFileUrl(sermon.video_url)} controls className="w-full aspect-video" />
        </div>
      )}

      <Tabs defaultValue="summary" className="mb-12">
        <TabsList className="bg-white border border-[#ECE6D8] p-1 rounded-full h-auto">
          <TabsTrigger value="summary" className="rounded-full data-[state=active]:bg-[#1B1C1A] data-[state=active]:text-white" data-testid="tab-summary">Resumen</TabsTrigger>
          <TabsTrigger value="transcript" className="rounded-full data-[state=active]:bg-[#1B1C1A] data-[state=active]:text-white" data-testid="tab-transcript">Transcripción</TabsTrigger>
          <TabsTrigger value="notes" className="rounded-full data-[state=active]:bg-[#1B1C1A] data-[state=active]:text-white" data-testid="tab-notes">Mis notas</TabsTrigger>
          <TabsTrigger value="body" className="rounded-full data-[state=active]:bg-[#1B1C1A] data-[state=active]:text-white" data-testid="tab-body">Prédica completa</TabsTrigger>
        </TabsList>
        <TabsContent value="summary" className="mt-6 space-y-8">
          {view.summary ? (
            <p className="font-display text-xl md:text-2xl leading-[1.5] text-[#1B1C1A] tracking-tight" data-testid="summary-text">{view.summary}</p>
          ) : (
            <p className="text-[#55594F] italic text-base">Sin resumen todavía.</p>
          )}
          {view.key_points?.length > 0 && (
            <div>
              <div className="eyebrow mb-3">Puntos clave</div>
              <ul className="space-y-3">
                {view.key_points.map((p, i) => (
                  <li key={i} className="flex gap-3 text-[#1B1C1A] text-lg">
                    <span className="font-mono text-[#A36A00] font-bold shrink-0 w-8">{String(i + 1).padStart(2, "0")}</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {view.memory_verse && (
            <div className="rounded-[24px] bg-[#FDFAF2] border-l-4 border-[#F5B841] p-6">
              <div className="eyebrow mb-2">Para memorizar</div>
              <p className="font-display text-xl md:text-2xl leading-snug text-[#1B1C1A] italic">{view.memory_verse}</p>
            </div>
          )}
          {view.group_questions?.length > 0 && (
            <div>
              <div className="eyebrow mb-3">Preguntas para grupos pequeños</div>
              <ol className="space-y-4 font-display text-lg md:text-xl text-[#1B1C1A] tracking-tight">
                {view.group_questions.map((q, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="font-mono text-[#A36A00] font-bold shrink-0 w-8">{String(i + 1).padStart(2, "0")}</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </TabsContent>
        <TabsContent value="transcript" className="mt-6">
          {sermon.transcription?.length > 0 ? (
            <div className="space-y-3 max-h-[520px] overflow-auto pr-2" data-testid="transcript-list">
              {sermon.transcription.map((seg, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="ts-chip" onClick={() => audioApi.current.seek?.(seg.start)} data-testid={`ts-${i}`}>
                    {fmtTs(seg.start)}
                  </span>
                  <p className="text-[#1B1C1A] text-base md:text-lg leading-relaxed flex-1">{seg.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#55594F] italic text-base">Sin transcripción todavía.</p>
          )}
        </TabsContent>
        <TabsContent value="notes" className="mt-6">
          <div className="text-base text-[#55594F] mb-3">Tus notas se guardan sólo en este dispositivo.</div>
          <textarea
            className="input-dark min-h-[280px] font-sans text-base md:text-lg leading-relaxed"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Escribí lo que Dios te está hablando…"
            data-testid="notes-area"
          />
        </TabsContent>
        <TabsContent value="body" className="mt-6">
          {view.body_html ? (
            <article className="font-sans text-lg leading-relaxed text-[#1B1C1A] space-y-4 max-w-none [&_h2]:font-display [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-[#F5B841] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#3F433C] [&_blockquote]:my-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: view.body_html }} />
          ) : (
            <p className="text-[#55594F] italic text-base">Sin texto completo todavía.</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
